import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  PenLine,
  TextSearch,
  Smile,
  Briefcase,
  FoldVertical,
  Type,
  List,
  Paperclip,
  Mic,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel
} from '@core/components/ui/dropdown-menu';
import { TextButton, FilledButton, TextArea } from '../../components';
import { useAi } from '../../components/providers/AiProvider';

// Templates object for AI actions
const TEMPLATES = {
  PROFESSIONAL: 16,
  SUMMARY: 17,
  FRIENDLY: 18,
  CONCISE: 19,
  KEY_POINTS: 20,
  SUGGEST_EDITS: 21,
  PROOF_READ: 22,
  AI_WRITER: 24
};

const AiMenu = ({ editor }) => {
  const [selectionActive, setSelectionActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [promptStage, setPromptStage] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [customFile, setCustomFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef(null);
  const mediaRecorder = useRef(null);
  const { handleAiRequest, handleVoiceRequest } = useAi();

  // Effect to listen for selection changes
  useEffect(() => {
    if (!editor) {
      return;
    }

    // Function to update selection state based on editor's current state
    const updateSelectionStatus = () => {
      setSelectionActive(!editor.state.selection.empty);
    };

    // Set the initial state
    updateSelectionStatus();

    // Subscribe to selection updates
    editor.on('selectionUpdate', updateSelectionStatus);
    // Subscribe to focus changes as selection might implicitly change
    editor.on('focus', updateSelectionStatus);
    editor.on('blur', updateSelectionStatus); // Update on blur too (selection might be lost)

    // Cleanup subscription on unmount
    return () => {
      editor.off('selectionUpdate', updateSelectionStatus);
      editor.off('focus', updateSelectionStatus);
      editor.off('blur', updateSelectionStatus);
    };
  }, [editor]); // Dependency array includes editor

  // Simplified handleAiAction - gets text inside
  const handleAiAction = useCallback(
    async (template, customPrompt = '') => {
      if (!editor || !handleAiRequest) return;

      // Close the dropdown menu
      setOpen(false);

      const { view, state } = editor;
      const { from, to } = view.state.selection;
      const selectedText = state.doc.textBetween(from, to, '\n');

      // Don't run actions requiring selection if there isn't one
      // Except for template AI_WRITER
      if (template !== TEMPLATES.AI_WRITER && from === to) {
        console.warn('AI action requires text selection.');
        return;
      }

      // For generate (AI_WRITER), insert at the current cursor position
      // and don't pass selectedText
      const textToProcess =
        template === TEMPLATES.AI_WRITER ? '' : selectedText;
      const originalContentForNode =
        template === TEMPLATES.AI_WRITER ? customPrompt : selectedText;

      try {
        // Insert the placeholder AI node
        editor
          .chain()
          .focus()
          .createAiNode({
            originalContent: originalContentForNode, // Use appropriate content
            content: '',
            status: 'starting'
          })
          .run();

        const response = await handleAiRequest({
          template,
          selectedText: textToProcess, // Pass empty string for generation
          customPrompt, // Pass the prompt for generation or custom edits
          customFileData: customFile // Pass attached pdf data
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch AI response (status: ${response.status})`
          );
        }

        const reader = response.body.getReader();
        let accumulatedText = '';
        let currentStatus = 'running'; // Track status for update

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            currentStatus = 'completed';
            break; // Exit loop when stream is done
          }

          const decodedChunk = new TextDecoder().decode(value, {
            stream: true
          });
          const lines = decodedChunk.split('\n');

          for (const line of lines) {
            if (line.trim() === '' || line === 'data: [DONE]') continue;

            try {
              const parsedLine = JSON.parse(line.replace(/^data: /, ''));
              if (parsedLine.choices && parsedLine.choices[0]?.delta?.content) {
                accumulatedText += parsedLine.choices[0].delta.content;
                // Update content progressively
                editor.commands.updateAiContent({
                  originalContent: originalContentForNode, // Ensure we target the correct node
                  content: accumulatedText,
                  status: 'running'
                });
              }
            } catch (e) {
              console.error('Failed to parse stream chunk:', line, e);
              // Optionally update status to error here
            }
          }
        }
        // Final update after loop finishes
        editor.commands.updateAiContent({
          originalContent: originalContentForNode,
          content: accumulatedText,
          status: currentStatus
        });
      } catch (error) {
        console.error('Error during AI request:', error);
        // Update the node status to rejected/error
        editor.commands.updateAiContent({
          originalContent: originalContentForNode,
          content: `Error: ${error.message}`, // Display error message
          status: 'rejected'
        });
      } finally {
        setCustomFile(null);
      }
    },
    [editor, handleAiRequest, customFile]
  );

  const handleVoice = useCallback(async () => {
    if (isProcessing) return;

    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        mediaRecorder.current = new MediaRecorder(stream);
        const audioChunks = [];

        mediaRecorder.current.ondataavailable = event => {
          audioChunks.push(event.data);
        };

        mediaRecorder.current.onstop = async () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });

          // Reset state
          setIsRecording(false);

          // Stop all tracks on the media stream
          stream.getTracks().forEach(track => track.stop());

          // Call handleVoiceRequest and set the text to the prompt
          try {
            setIsProcessing(true);
            const { text } = await handleVoiceRequest(audioBlob);
            setCustomPrompt(prev => prev + (prev ? ' ' : '') + text);
          } catch (error) {
            console.error('Error processing voice request:', error);
          } finally {
            setIsProcessing(false);
          }
        };

        mediaRecorder.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        setIsRecording(false);
        setIsProcessing(false);
      }
    } else {
      if (
        mediaRecorder.current &&
        mediaRecorder.current.state === 'recording'
      ) {
        mediaRecorder.current.stop();
      }
    }
  }, [isRecording, isProcessing, handleVoiceRequest]);

  if (!editor) return null;

  // Helper for DropdownMenuItem onSelect
  const handleSelect = template => e => {
    e.preventDefault(); // Prevent focus loss
    if (
      template === TEMPLATES.SUGGEST_EDITS ||
      template === TEMPLATES.AI_WRITER
    ) {
      // Transition to prompt stage
      console.log('set template:', template);
      setActiveTemplate(template);
      setPromptStage(true);
    } else {
      handleAiAction(template);
    }
  };

  const handleGenerate = () => {
    handleAiAction(activeTemplate, customPrompt);
    setCustomPrompt(''); // Clear the prompt after use
    setCustomFile(null); // Clear the file after use
    setPromptStage(false); // Return to main menu after generation
  };

  const handleBack = () => {
    setPromptStage(false); // Return to main menu
    setCustomFile(null);
    setFileError('');
    setActiveTemplate(null);
  };

  const handleFileChange = e => {
    setFileError('');
    const file = e.target.files[0];

    if (!file) return;

    // Check if file is PDF
    if (!file.type.includes('pdf')) {
      setFileError('Only PDF files are allowed');
      return;
    }

    // Check file size (32MB = 32 * 1024 * 1024 bytes)
    if (file.size > 32 * 1024 * 1024) {
      setFileError('File must be less than 32MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      const base64String = event.target.result.split(',')[1];
      setCustomFile({
        name: file.name,
        data: base64String
      });
    };
    reader.readAsDataURL(file);
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const removeFile = () => {
    setCustomFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className='p-2 rounded hover:bg-base-200' title='AI Assistant'>
          <img
            src='/+Ai_Logo.svg'
            alt='AI Assistant'
            className='w-[18px] h-[18px]'
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56'>
        {promptStage ? (
          <div className='flex flex-col'>
            <div className='flex justify-between items-center mb-2'>
              <TextButton
                onClick={handleBack}
                className='self-start'
                colour='base'
              >
                ← Back
              </TextButton>
              <button
                onClick={handleVoice}
                className={`p-2 rounded hover:bg-base-200 ${
                  isRecording ? 'bg-base-200' : ''
                }`}
                title='Voice Input'
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 size={18} className='animate-spin' />
                ) : isRecording ? (
                  <div className='w-[15px] h-[15px] rounded-full bg-red-500 animate-pulse'></div>
                ) : (
                  <Mic size={18} />
                )}
              </button>
            </div>
            <TextArea
              value={customPrompt}
              onChange={e => setCustomPrompt(e.target.value)}
              placeholder='Enter your prompt...'
              className='mb-2'
            />
            {/* File attachment section */}
            <div className='flex flex-col mb-2'>
              <input
                type='file'
                accept='.pdf'
                ref={fileInputRef}
                onChange={handleFileChange}
                className='hidden'
              />

              {customFile ? (
                <div className='flex items-center justify-between p-2 border rounded mb-1'>
                  <span className='text-sm truncate max-w-[180px]'>
                    {customFile.name}
                  </span>
                  <TextButton
                    onClick={removeFile}
                    colour='error'
                    className='text-xs'
                  >
                    Remove
                  </TextButton>
                </div>
              ) : (
                <TextButton
                  onClick={handleFileClick}
                  colour='base'
                  className='flex items-center justify-center mb-1'
                  leftIcon={<Paperclip size={14} />}
                >
                  Attach PDF (optional)
                </TextButton>
              )}

              {fileError && (
                <span className='text-xs text-red-500 mb-1'>{fileError}</span>
              )}
            </div>
            <FilledButton
              onClick={handleGenerate}
              className='justify-center'
              colour='primary'
              leftIcon={<Sparkles />}
            >
              Generate
            </FilledButton>
          </div>
        ) : (
          <>
            {selectionActive ? (
              <DropdownMenuGroup>
                <DropdownMenuLabel className='flex items-center gap-2'>
                  <Sparkles size={16} /> AI Actions
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={handleSelect(TEMPLATES.SUGGEST_EDITS)}
                >
                  <Sparkles size={16} className='mr-2' /> Suggest Edits
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.PROOF_READ)}>
                  <TextSearch size={16} className='mr-2' /> Proof Read
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Tone</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.FRIENDLY)}>
                  <Smile size={16} className='mr-2' /> Friendly
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleSelect(TEMPLATES.PROFESSIONAL)}
                >
                  <Briefcase size={16} className='mr-2' /> Professional
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.CONCISE)}>
                  <FoldVertical size={16} className='mr-2' /> Concise
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Summarize</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.SUMMARY)}>
                  <Type size={16} className='mr-2' /> Summary
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.KEY_POINTS)}>
                  <List size={16} className='mr-2' /> Key Points
                </DropdownMenuItem>
              </DropdownMenuGroup>
            ) : (
              <DropdownMenuGroup>
                <DropdownMenuLabel className='flex items-center gap-2'>
                  <Sparkles size={16} /> AI Assistant
                </DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.AI_WRITER)}>
                  <PenLine size={16} className='mr-2' /> Prompt
                </DropdownMenuItem>
              </DropdownMenuGroup>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AiMenu;
